import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertTriangle, Check, Crown, ChevronLeft, RotateCw, Ticket, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BottomNavigation from "@/components/BottomNavigation";
import { toast } from "@/components/ui/use-toast";
import PurchaseService, { TRIAL_PERIOD_DAYS } from "@/services/PurchaseService";
import { Capacitor } from "@capacitor/core";
import { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { adminAPI } from "@/api/admin";
import useSubscription, { Plan } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import MobileStoreRedirect from "@/components/ui/MobileStoreRedirect";
import { useTranslation } from "react-i18next";
import { ScreenHeader, MonoValue } from "@/components/redesign";

interface SubscriptionPageProps {
  isExpired?: boolean;
}

interface PurchaseResult {
  success: boolean;
  cancelled?: boolean;
  error?: string;
  result?: any;
}

const SubscriptionPage: React.FC<SubscriptionPageProps> = ({ isExpired = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { status, purchasePlan, restorePurchases, redeemCode, refreshSubscription } = useSubscription();
  const { signOut } = useAuth();
  const { isLoading, isSubscribed, activePlan, planType, plans } = status;
  const [promoCode, setPromoCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [hasAttemptedAutoFix, setHasAttemptedAutoFix] = useState(false);
  const isMobilePlatform = Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';

  // Get message from location state if available
  const message = location.state?.message;
  const returnPath = location.state?.returnPath;

  // Debug logging for subscription status
  useEffect(() => {

  }, [isLoading, isSubscribed, activePlan, planType, status.has_access, status.plan, plans]);

  // Auto-fix effect: automatically attempt to fix RevenueCat connection if no plans are available
  useEffect(() => {
    const attemptAutoFix = async () => {
      // Only attempt auto-fix on mobile platforms
      if (!isMobilePlatform || hasAttemptedAutoFix || isLoading) {
        return;
      }

      // If we have no plans and we're not currently loading, attempt auto-fix
      if (plans.length === 0) {

        setHasAttemptedAutoFix(true);

        try {
          const success = await PurchaseService.forceLoginFix();

          if (success) {

            // Wait a moment for RevenueCat to settle
            await new Promise(resolve => setTimeout(resolve, 1500));
            // Refresh subscription data
            await refreshSubscription();
          } else {

          }
        } catch (error) {

        }
      }
    };

    // Run auto-fix with a small delay to allow initial loading to complete
    const timer = setTimeout(attemptAutoFix, 2000);

    return () => clearTimeout(timer);
  }, [isMobilePlatform, plans.length, isLoading, hasAttemptedAutoFix, refreshSubscription]);

  // Display error message if present
  const renderErrorMessage = () => {
    if (!message) return null;

    return (
      <div className="bg-error-50 border border-error-100 text-error-600 rounded-xl p-4 mb-5 flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-[14px]">{t('subscription.project_limit_exceeded')}</p>
          <p className="text-[13px] mt-0.5">{message}</p>
        </div>
      </div>
    );
  };

  // Handle purchase of a plan
  const handlePurchase = async (plan: Plan) => {
    if (!plan.identifier) {
      toast({
        title: t('subscription.purchase_error'),
        description: t('subscription.plan_identifier_not_found'),
        variant: "destructive"
      });
      return;
    }

    try {
      setIsPurchasing(true);
      setSelectedPlan(plan.identifier);


      const success = await purchasePlan(plan);

      if (success) {
        toast({
          title: t('subscription.purchase_successful'),
          description: t('subscription.subscription_activated'),
        });

        // Force a refresh of the subscription data after successful purchase

        await refreshSubscription();

        // Redirect back to the return path if available
        if (returnPath) {
          navigate(returnPath);
        }
      } else {
        toast({
          title: t('subscription.purchase_failed'),
          description: t('subscription.payment_not_completed'),
          variant: "destructive"
        });
      }
    } catch (error) {


      // Don't show error for user cancellation
      if (error instanceof Error && error.message === 'User cancelled the purchase') {
        toast({
          title: t('subscription.purchase_cancelled'),
          description: t('subscription.purchase_cancelled_desc'),
        });
      } else {
        toast({
          title: t('subscription.purchase_error'),
          description: t('subscription.payment_error'),
          variant: "destructive"
        });
      }
    } finally {
      setIsPurchasing(false);
      setSelectedPlan(null);
    }
  };

  // Handle restore purchases
  const handleRestore = async () => {
    try {
      setIsRestoring(true);
      const hasRestoredPurchases = await restorePurchases();

      if (hasRestoredPurchases) {
        // Force a refresh of the subscription data after successful restore

        await PurchaseService.refreshSubscription();
        await refreshSubscription();

        toast({
          title: t('subscription.purchases_restored'),
          description: t('subscription.purchases_restored_desc'),
        });

        // Redirect back to the return path if available
        if (returnPath) {
          navigate(returnPath);
        }
      } else {
        toast({
          title: t('subscription.restore_completed'),
          description: t('subscription.no_active_subscription'),
        });
      }
    } catch (error) {

      toast({
        title: t('subscription.restore_error'),
        description: t('subscription.restore_error_desc'),
        variant: "destructive"
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Handle redeem promo code
  const handleRedeemCode = async () => {
    if (!promoCode.trim()) {
      toast({
        title: t('common.error'),
        description: t('subscription.promo_code_required'),
        variant: "destructive"
      });
      return;
    }

    try {
      setIsRedeeming(true);
      const success = await redeemCode(promoCode);

      if (success) {
        toast({
          title: t('subscription.code_redeemed'),
          description: t('subscription.code_redeemed_desc'),
        });
        setPromoCode('');

        // Redirect back to the return path if available
        if (returnPath) {
          navigate(returnPath);
        }
      }
    } catch (error) {

      toast({
        title: t('subscription.redeem_error'),
        description: t('subscription.redeem_error_desc'),
        variant: "destructive"
      });
    } finally {
      setIsRedeeming(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut();
      toast({
        title: t('subscription.logged_out'),
        description: t('subscription.logged_out_desc'),
      });
    } catch (error) {
      toast({
        title: t('subscription.logout_error'),
        description: t('subscription.logout_error_desc'),
        variant: "destructive"
      });
    }
  };

  // Handle force login fix for RevenueCat
  const handleForceLoginFix = async () => {
    try {
      setIsRestoring(true);
      // Reset auto-fix flag to allow manual retry
      setHasAttemptedAutoFix(false);

      toast({
        title: t('subscription.revenuecat_fixing'),
        description: t('subscription.please_wait'),
      });

      const success = await PurchaseService.forceLoginFix();

      if (success) {
        toast({
          title: t('subscription.revenuecat_fixed'),
          description: t('subscription.revenuecat_fixed_desc'),
        });

        // Force refresh subscription after successful login fix
        await refreshSubscription();
      } else {
        toast({
          title: t('subscription.login_fix_failed'),
          description: t('subscription.login_fix_failed_desc'),
          variant: "destructive"
        });
      }
    } catch (error) {

      toast({
        title: t('subscription.login_fix_error'),
        description: t('subscription.login_fix_error_desc'),
        variant: "destructive"
      });
    } finally {
      setIsRestoring(false);
    }
  };

  // Check if a plan is the current active plan
  const isCurrentPlan = (plan: Plan) => {
    // If user doesn't have subscription access, no plan is current
    if (!isSubscribed || status.has_access === false) {

      return false;
    }

    // Get the plan from status or activePlan
    const currentPlan = status.plan || activePlan;
    if (!currentPlan) {

      return false;
    }

    // Match by plan name (case-insensitive) with more robust checking
    const currentPlanLower = currentPlan.toLowerCase();
    const planNameLower = plan.name.toLowerCase();

    // Direct name match
    if (currentPlanLower === planNameLower) {

      return true;
    }

    // Plan type matching with various formats
    const isPremiumPlan = currentPlanLower.includes('premium') || currentPlanLower === 'pro';
    const isElitePlan = currentPlanLower.includes('elite') || currentPlanLower.includes('ultimate');

    const isMatch = (isPremiumPlan && planNameLower.includes('premium')) ||
                   (isElitePlan && planNameLower.includes('elite'));



    return isMatch;
  };

  // Get button text and variant based on plan status
  const getPlanButtonConfig = (plan: Plan) => {
    // If this is the current active plan, show as current
    if (isCurrentPlan(plan)) {
      return {
        text: t('subscription.current_plan_yours'),
        variant: "secondary" as const,
        disabled: true,
        hidden: false
      };
    }

    // If user has an active subscription, determine upgrade/downgrade
    if (isSubscribed && status.has_access === true) {
      const currentPlanPrice = plans.find(p => isCurrentPlan(p))?.price || 0;
      const isUpgrade = plan.price > currentPlanPrice;

      return {
        text: isUpgrade ? t('subscription.upgrade') : t('subscription.downgrade'),
        variant: "default" as const,
        disabled: false,
        hidden: false
      };
    }

    // For users without active subscription
    return {
      text: t('subscription.subscribe'),
      variant: "default" as const,
      disabled: false,
      hidden: false
    };
  };

  // Render subscription status
  const renderSubscriptionStatus = () => {
    if (isSubscribed && status.has_access === true) {
      // Get the current plan details
      const currentPlan = plans.find(plan => isCurrentPlan(plan));
      const currentPlanName = currentPlan?.name || status.plan || activePlan || 'Premium';

      return (
        <div className="bg-brand-50 border border-brand-200 rounded-2xl p-5 mb-5 text-center">
          <div className="bg-brand-100 rounded-full inline-flex items-center justify-center p-3 mb-3">
            <Crown className="text-brand-600 h-7 w-7" />
          </div>
          <h2 className="text-[18px] font-bold text-gray-900 mb-1">
            {currentPlanName} {t('subscription.subscription_active')}
          </h2>
          <p className="text-[13px] text-gray-500">
            {t('subscription.full_access')}
          </p>
          {/* Show current plan details */}
          {currentPlan && (
            <div className="mt-4 p-3 bg-white rounded-xl border border-brand-200">
              <div className="flex items-center justify-between">
                <div className="text-left">
                  <p className="text-[14px] font-semibold text-brand-700">{currentPlan.name} {t('subscription.package')}</p>
                  <p className="text-[12px] text-gray-500">{currentPlan.projects} {t('subscription.active_project')}</p>
                </div>
                <div className="text-right">
                  <MonoValue value={currentPlan.priceString} size="md" tone="brand" />
                  <p className="text-[11px] text-gray-400">{t('subscription.monthly')}</p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-brand-100">
                <p className="text-[11px] text-gray-400 text-center">
                  {t('subscription.active_subscription_features')}
                </p>
              </div>
            </div>
          )}
        </div>
      );
    } else if (status.has_access === true) {
      // Free tier — lifetime access, no expiry. Not a trial, not expired.
      return (
        <div className="bg-brand-50 border border-brand-200 rounded-2xl p-5 mb-5 text-center">
          <div className="bg-brand-100 rounded-full inline-flex items-center justify-center p-3 mb-3">
            <Check className="text-brand-600 h-7 w-7" />
          </div>
          <h2 className="text-[18px] font-bold text-gray-900 mb-1">
            {t('subscription.current_plan')}
          </h2>
          <p className="text-[13px] text-gray-500">
            {t('subscription.premium_features_access')}
          </p>
          <div className="mt-4 p-3 bg-white rounded-xl border border-brand-200">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <p className="text-[14px] font-semibold text-gray-900">{t('subscription.free')} {t('subscription.package')}</p>
                <p className="text-[12px] text-gray-500">1 {t('subscription.active_project')}</p>
              </div>
              <div className="text-right">
                <p className="text-[14px] font-semibold text-gray-900">{t('subscription.free')}</p>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="bg-error-50 border border-error-100 rounded-2xl p-5 mb-5 text-center">
          <div className="bg-error-100 rounded-full inline-flex items-center justify-center p-3 mb-3">
            <AlertTriangle className="text-error-600 h-7 w-7" />
          </div>
          <h2 className="text-[18px] font-bold text-gray-900 mb-1">
            {t('subscription.subscription_expired')}
          </h2>
          <p className="text-[13px] text-gray-500">
            {t('subscription.subscription_required_continue')}
          </p>
        </div>
      );
    }
  };

  // Render plan purchase button based on platform
  const renderPurchaseButton = (plan: Plan) => {
    if (plan.requiresMobile && !isMobilePlatform) {
      return (
        <div>
          <Button
            variant="outline"
            className="w-full mb-3 border-gray-200 text-gray-500 rounded-lg"
            disabled={true}
          >
            {t('subscription.mobile_device_required')}
          </Button>
          <MobileStoreRedirect variant="compact" />
        </div>
      );
    }

    const buttonConfig = getPlanButtonConfig(plan);
    const isCurrent = isCurrentPlan(plan);

    return (
      <Button
        onClick={() => !buttonConfig.disabled && handlePurchase(plan)}
        className={`w-full rounded-lg h-11 font-semibold ${isCurrent ? 'bg-brand-50 text-brand-700 border border-brand-300 hover:bg-brand-100' : 'bg-brand-600 hover:bg-brand-700 text-white'}`}
        variant={isCurrent ? "outline" : buttonConfig.variant}
        disabled={buttonConfig.disabled || isPurchasing || isRestoring || !plan.identifier}
      >
        {isPurchasing && selectedPlan === plan.identifier ? (
          <span className="flex items-center">
            <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2"></span>
            {t('subscription.processing')}
          </span>
        ) : (
          <>
            {isCurrent && <Crown className="h-4 w-4 mr-2" />}
            {buttonConfig.text}
            {isCurrent && <Check className="h-4 w-4 ml-2" />}
          </>
        )}
      </Button>
    );
  };

  return (
    <div className="page-container bg-gray-50">
      <ScreenHeader
        title={t('subscription.page_title')}
        showBack={true}
        right={
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setHasAttemptedAutoFix(false);
                refreshSubscription();
              }}
              className="p-1.5 rounded-md text-brand-25 hover:text-white"
            >
              <RotateCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-[13px] text-brand-25 hover:text-white px-2 py-1 rounded-md"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t('subscription.logout')}</span>
            </button>
          </div>
        }
      />

      <main className="flex-1 px-4 py-4 overflow-y-auto pb-20 space-y-4">
        {/* Error message */}
        {renderErrorMessage()}

        {/* Mobile store redirect for PC users who need to subscribe (no access). */}
        {!isMobilePlatform && (isExpired || status.has_access === false) && (
          <div className="mb-2">
            <MobileStoreRedirect variant="detailed" />
          </div>
        )}

        {/* Subscription status */}
        {renderSubscriptionStatus()}

        {/* Plans */}
        <div className="space-y-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500 px-0.5">
            {t('subscription.subscription_plans')}
          </p>

          {isLoading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            plans.map((plan, index) => {
              const isCurrent = isCurrentPlan(plan);
              const buttonConfig = getPlanButtonConfig(plan);

              return (
                <div
                  key={index}
                  className={`bg-white border rounded-2xl p-5 shadow-sm transition-all ${
                    isCurrent
                      ? 'border-brand-400 shadow-md ring-1 ring-brand-200'
                      : 'border-gray-200 hover:border-brand-200'
                  }`}
                >
                  <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <h3 className="text-[17px] font-bold text-gray-900 flex items-center gap-2">
                        {plan.name}
                        {isCurrent && (
                          <span className="bg-brand-50 text-brand-600 text-[10px] font-bold px-2.5 py-0.5 rounded-full border border-brand-200">
                            {t('subscription.active_plan')}
                          </span>
                        )}
                      </h3>
                      <p className="text-[13px] text-gray-500 mt-0.5">{plan.projects} {t('subscription.active_project')}</p>
                    </div>
                    <div className="text-right">
                      <MonoValue
                        value={plan.priceString}
                        size="md"
                        tone={isCurrent ? "brand" : "default"}
                      />
                      <p className="text-[11px] text-gray-400">{t('subscription.monthly')}</p>
                    </div>
                  </div>

                  <div className="mb-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-500 mb-2">{t('subscription.features')}</p>
                    <ul className="space-y-1.5">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-[13px] text-gray-700">
                          <Check className={`h-4 w-4 shrink-0 ${isCurrent ? 'text-brand-600' : 'text-success-600'}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {renderPurchaseButton(plan)}
                </div>
              );
            })
          )}

          {/* Add retry button if packages don't have packageObject */}
          {plans.length > 0 && plans.some(plan => !plan.packageObject) && (
            <div className="mt-2">
              <Button
                variant="outline"
                className="w-full flex items-center justify-center border-gray-200 text-gray-700 rounded-lg"
                onClick={() => {
                  setHasAttemptedAutoFix(false);
                  refreshSubscription();
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <span className="animate-spin h-4 w-4 border-2 border-brand-600 border-t-transparent rounded-full mr-2"></span>
                    {t('subscription.refreshing')}
                  </span>
                ) : (
                  <>
                    <RotateCw className="h-4 w-4 mr-2" />
                    {t('subscription.refresh_payment_system')}
                  </>
                )}
              </Button>
              <p className="text-[11px] text-center text-gray-400 mt-2">
                {t('subscription.package_info_unavailable')}
              </p>
            </div>
          )}
        </div>

        {/* Restore purchases button */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
          <Button
            variant="outline"
            className="w-full flex items-center justify-center border-gray-200 text-gray-700 hover:bg-gray-50 rounded-lg"
            onClick={handleRestore}
            disabled={isRestoring || isPurchasing}
          >
            {isRestoring ? (
              <span className="flex items-center">
                <span className="animate-spin h-4 w-4 border-2 border-brand-600 border-t-transparent rounded-full mr-2"></span>
                {t('subscription.restoring')}
              </span>
            ) : (
              <>
                <RotateCw className="h-4 w-4 mr-2" />
                {t('subscription.restore_previous_purchases')}
              </>
            )}
          </Button>
        </div>

        {/* Force login fix button - only show on mobile if no offerings */}
        {isMobilePlatform && plans.length === 0 && (
          <div className="bg-warning-50 border border-warning-300 rounded-2xl p-4">
            <Button
              variant="outline"
              className="w-full flex items-center justify-center border-warning-400 text-warning-600 hover:bg-warning-50 rounded-lg"
              onClick={handleForceLoginFix}
              disabled={isRestoring || isPurchasing}
            >
              {isRestoring ? (
                <span className="flex items-center">
                  <span className="animate-spin h-4 w-4 border-2 border-warning-600 border-t-transparent rounded-full mr-2"></span>
                  {t('subscription.fixing')}
                </span>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {t('subscription.fix_revenuecat_login')}
                </>
              )}
            </Button>
            <p className="text-[11px] text-center text-warning-600 mt-2">
              {t('subscription.packages_not_visible')}
            </p>
          </div>
        )}

        {/* Promo code section */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500 mb-3">{t('subscription.promo_code')}</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <Ticket className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                placeholder={t('subscription.enter_promo_code')}
                className="pl-9 border-gray-200 rounded-lg focus-visible:ring-brand-300 focus-visible:border-brand-600"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </div>
            <Button
              onClick={handleRedeemCode}
              disabled={!promoCode.trim() || isRedeeming || isPurchasing}
              className="bg-brand-600 hover:bg-brand-700 text-white rounded-lg shrink-0 w-full sm:w-auto"
            >
              {isRedeeming ? (
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
              ) : (
                t('subscription.use_code')
              )}
            </Button>
          </div>
        </div>

        {/* Logout section */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500 mb-3">{t('subscription.account')}</p>
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full flex items-center justify-center gap-2 border-error-600 text-error-600 hover:bg-error-50 rounded-lg"
          >
            <LogOut className="h-4 w-4" />
            {t('subscription.logout_button')}
          </Button>
        </div>

        <p className="text-[11px] text-center text-gray-400 pb-2">
          {t('subscription.payment_billing_info', { platform: Capacitor.getPlatform() === 'ios' ? 'Apple ID' : 'Google Play' })}
        </p>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default SubscriptionPage;
